import { useEffect, useState } from "react";
import PageMeta from "@/components/common/PageMeta";
import DailyLoggingCard from "./DailyLoggingCard";
import NeedsAttentionPanel from "./NeedsAttentionPanel";
import FlocksToActOn from "@/components/common/FlocksToActOn";
import { dashboardService } from "@/services/dashboardService";
import { alertsService } from "@/services/alertsService";
import { handleApiError } from "@/services/api";
import type { Alert, DashboardDailyLogging } from "@/types/models";

// The dashboard for a manager or supervisor outside daily-report mode: what is
// missing from the daily book and what needs doing in the sheds, with no money
// in it. The owner's summary (P&L, cost per egg, overdue money) is owner/viewer
// only in the API, and showing staff that page used to render an error and
// nothing else. The server filters the alerts to the no-money rules.
export default function StaffDashboard() {
  const [logging, setLogging] = useState<DashboardDailyLogging | null>(null);
  const [loggingError, setLoggingError] = useState("");
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertsError, setAlertsError] = useState(false);

  useEffect(() => {
    dashboardService
      .getDailyLogging()
      .then((l) => {
        setLogging(l);
        setLoggingError("");
      })
      .catch((e) => setLoggingError(handleApiError(e)));
    alertsService
      .getAlerts()
      .then((a) => {
        setAlerts(a);
        setAlertsError(false);
      })
      .catch(() => setAlertsError(true));
  }, []);

  return (
    <>
      <PageMeta title="Dashboard | Poultry Book" description="Poultry farm management dashboard" />
      {loggingError && (
        <div className="card mb-6 p-6">
          <p className="text-error-500">{loggingError}</p>
        </div>
      )}
      {logging && (
        <DailyLoggingCard
          daysBehind={logging.freshness.days_behind}
          lastLogDate={logging.freshness.last_log_date}
          entries={logging.missing_entries ?? []}
          canLog
        />
      )}
      {/* No snooze control: muting an alert silences it for the owner too. */}
      <FlocksToActOn alerts={alerts} />
      <NeedsAttentionPanel alerts={alerts} error={alertsError} />
    </>
  );
}
